/**
 * The Files tab on one file (`{ tab: 'files', focus: { path } }`, `?tab=files&file=<path>`): a file outside every
 * service (the Brain, an input, a result, a screenshot, `.flow/…`, the root's README) opens here, in place of the
 * table, instead of in Code. Markdown rendered as the editor's preview renders it (markdown/MarkdownView.jsx), an image
 * shown, other text in a read view; a file it cannot show (a PDF, a zip, a text too large) says so and offers Download.
 * "All files" goes back to the table (the title's "Files" crumb too, WorkspaceView); an editor gets "Edit in Code".
 */
import React from 'react';
import PropTypes from 'prop-types';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { agentReads } from '../../utils/filesModel';
import { ageOf, formatBytes } from '../home/FilesSection';
import MarkdownView from '../markdown/MarkdownView';
import { useFileContent } from './FilePreview';

export default function FileView({ projectId, row, onBack, onDownload, onEdit = null, onOpenFile = null }) {
  const { kind, content } = useFileContent(projectId, row);
  const facts = [row.dir, formatBytes(row.size), ageOf(row.modified) ? `added ${ageOf(row.modified)}` : null, agentReads(row) ? 'the agent reads it' : null].filter(Boolean);
  return (
    <article aria-label={row.path} className="files-view" data-testid="files-view">
      <div className="files-view-bar">
        <button className="files-link files-view-back" data-testid="files-view-back" onClick={onBack} type="button">
          <ArrowLeftIcon aria-hidden size={14} /> All files
        </button>
        <span className="files-view-facts">{facts.join(' · ')}</span>
        <span className="files-view-actions">
          {onEdit && <button className="files-btn" data-testid="files-view-edit" onClick={() => onEdit(row.path)} type="button">Edit in Code</button>}
          <button className="files-btn" data-testid="files-view-download" onClick={() => onDownload(row)} type="button">Download</button>
        </span>
      </div>
      <div className={`files-view-body is-${kind}`}>
        {content.status === 'loading' && <p className="files-hint">Loading {row.name}…</p>}
        {content.status === 'error' && <p className="files-hint" role="alert">{row.name} could not be read: {content.error}</p>}
        {content.status === 'none' && (
          <p className="files-hint">{kind === 'large' ? 'This file is too large to show here.' : 'This kind of file cannot be shown here.'} Download it to open it.</p>
        )}
        {content.status === 'ready' && kind === 'image' && <img alt={row.name} className="files-view-image" data-testid="files-view-image" src={content.value} />}
        {content.status === 'ready' && kind === 'markdown' && (
          <MarkdownView className="files-view-md" filePath={row.path} onOpenFile={onOpenFile} projectId={projectId} source={content.value} testId="files-view-md" />
        )}
        {content.status === 'ready' && kind === 'text' && <pre className="files-view-text" data-testid="files-view-text">{content.value}</pre>}
      </div>
    </article>
  );
}

FileView.propTypes = {
  onBack: PropTypes.func.isRequired,
  onDownload: PropTypes.func.isRequired,
  // (path) => void: the file in Code, to edit it; none (a viewer, Code not in the plan): no Edit in Code.
  onEdit: PropTypes.func,
  // (path) => void: a link to another project file in a Markdown file.
  onOpenFile: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  row: PropTypes.shape({
    dir: PropTypes.string, kind: PropTypes.string, modified: PropTypes.string, name: PropTypes.string, path: PropTypes.string.isRequired, size: PropTypes.number,
  }).isRequired,
};
